import { Editor } from '@tiptap/react';
import { Tooltip } from '@mantine/core';
import {
  IconBold,
  IconItalic,
  IconLink,
  IconUnlink,
  IconPhoto,
  IconBlockquote,
  IconList,
  IconListNumbers,
  IconH1,
  IconH2,
  IconH3,
  IconTable,
  IconCode,
  IconRowInsertBottom,
  IconRowRemove,
  IconColumnInsertRight,
  IconColumnRemove,
  IconTableOff,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './Toolbar.module.css';

interface ToolbarProps {
  editor: Editor;
  onImageUpload?: (file: File) => Promise<string | undefined>;
}

export const Toolbar = ({ editor, onImageUpload }: ToolbarProps) => {
  const { t } = useTranslation();
  // 使用状态强制组件在编辑器状态改变时重新渲染
  const [, setUpdateCounter] = useState(0);

  // 监听编辑器的选区和事务变化，强制 Toolbar 重新渲染以更新按钮状态
  useEffect(() => {
    const handleUpdate = () => {
      setUpdateCounter((prev) => prev + 1);
    };

    // 监听选区变化和内容更新
    editor.on('selectionUpdate', handleUpdate);
    editor.on('transaction', handleUpdate);

    return () => {
      editor.off('selectionUpdate', handleUpdate);
      editor.off('transaction', handleUpdate);
    };
  }, [editor]);

  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file && onImageUpload) {
        const url = await onImageUpload(file);
        if (url) {
          editor.chain().focus().setImage({ src: url }).run();
        }
      }
    };
    input.click();
  };

  // 添加链接
  const handleAddLink = () => {
    const url = window.prompt(t('doc.link.linkPlaceholder'));

    // 用户点击取消或输入空字符串
    if (!url) {
      return;
    }

    // 添加链接
    editor.chain().focus().setLink({ href: url }).run();
  };

  // 移除链接
  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
  };

  const ToolbarButton = ({
    icon,
    title,
    onClick,
    isActive,
  }: {
    icon: React.ReactNode;
    title: string;
    onClick: () => void;
    isActive?: boolean;
  }) => (
    <Tooltip label={title} withArrow>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault(); // 阻止默认行为，防止表单提交
          e.stopPropagation(); // 阻止事件冒泡
          onClick();
        }}
        onMouseDown={(e) => {
          e.preventDefault(); // 阻止鼠标按下时的默认行为，防止编辑器失焦
          e.stopPropagation(); // 阻止事件冒泡
        }}
        className={`${classes.toolbarButton} ${isActive ? classes.toolbarButtonActive : ''}`}
      >
        {icon}
      </button>
    </Tooltip>
  );

  const Separator = () => <div className={classes.separator} />;

  return (
    <div className={classes.toolbar}>
      {/* 文本格式 */}
      <ToolbarButton
        icon={<IconBold size={18} />}
        title={t('doc.toolbar.bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
      />
      <ToolbarButton
        icon={<IconItalic size={18} />}
        title={t('doc.toolbar.italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
      />

      <Separator />

      {/* 标题 */}
      <ToolbarButton
        icon={<IconH1 size={18} />}
        title={t('doc.toolbar.heading_one')}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive('heading', { level: 1 })}
      />
      <ToolbarButton
        icon={<IconH2 size={18} />}
        title={t('doc.toolbar.heading_two')}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
      />
      <ToolbarButton
        icon={<IconH3 size={18} />}
        title={t('doc.toolbar.heading_three')}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive('heading', { level: 3 })}
      />

      <Separator />

      {/* 链接和图片 */}
      {editor.isActive('link') ? (
        <ToolbarButton
          icon={<IconUnlink size={18} />}
          title={t('doc.toolbar.unlink')}
          onClick={handleRemoveLink}
          isActive={true}
        />
      ) : (
        <ToolbarButton
          icon={<IconLink size={18} />}
          title={t('doc.toolbar.link')}
          onClick={handleAddLink}
          isActive={false}
        />
      )}
      <ToolbarButton icon={<IconPhoto size={18} />} title={t('doc.toolbar.image')} onClick={handleImageUpload} />

      <Separator />

      {/* 列表 */}
      <ToolbarButton
        icon={<IconBlockquote size={18} />}
        title={t('doc.toolbar.blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
      />
      <ToolbarButton
        icon={<IconList size={18} />}
        title={t('doc.toolbar.unordered_list')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
      />
      <ToolbarButton
        icon={<IconListNumbers size={18} />}
        title={t('doc.toolbar.ordered_list')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
      />

      <Separator />

      {/* 代码和表格 */}
      <ToolbarButton
        icon={<IconCode size={18} />}
        title={t('doc.toolbar.codeblock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
      />
      {editor.isActive('table') ? (
        <>
          <ToolbarButton
            icon={<IconRowInsertBottom size={18} />}
            title={t('doc.toolbar.table_add_row')}
            onClick={() => editor.chain().focus().addRowAfter().run()}
          />
          <ToolbarButton
            icon={<IconColumnInsertRight size={18} />}
            title={t('doc.toolbar.table_add_col')}
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          />
          <ToolbarButton
            icon={<IconRowRemove size={18} />}
            title={t('doc.toolbar.table_del_row')}
            onClick={() => editor.chain().focus().deleteRow().run()}
          />
          <ToolbarButton
            icon={<IconColumnRemove size={18} />}
            title={t('doc.toolbar.table_del_col')}
            onClick={() => editor.chain().focus().deleteColumn().run()}
          />
          <ToolbarButton
            icon={<IconTableOff size={18} />}
            title={t('doc.toolbar.table_delete')}
            onClick={() => editor.chain().focus().deleteTable().run()}
          />
        </>
      ) : (
        <ToolbarButton
          icon={<IconTable size={18} />}
          title={t('doc.toolbar.table')}
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        />
      )}
    </div>
  );
};
